import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import api, { API, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import MessagingPanel from "../components/MessagingPanel";
import ChangePasswordDialog from "../components/ChangePasswordDialog";
import SignatureGate from "../components/SignatureGate";
import NotificationToggle from "../components/NotificationToggle";
import { ArabicLevelBlock } from "../components/ArabicLevelFields";
import { SOURATES, LEVEL_LABELS } from "../lib/sourates";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { BookOpenCheck, Trash2, Upload, Plus, History, MessageSquare, BookOpen, KeyRound, Wallet, CheckCircle2, Pencil } from "lucide-react";
import { toast } from "sonner";

const ML = { arabe: "Arabe", coran: "Coran", education_musulmane: "Éd. Musulmane" };

export default function TeacherDashboard() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [active, setActive] = useState(null);
  const [students, setStudents] = useState([]);
  const [grades, setGrades] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [homeworks, setHomeworks] = useState([]);
  const [tab, setTab] = useState("courses");
  const tabsListRef = useRef(null);

  useEffect(() => { api.get("/teacher/courses").then((r) => setCourses(r.data)); }, []);
  useEffect(() => {
    if (!active) return;
    api.get(`/enrollments/course/${active.id}`).then((r) => setStudents(r.data));
    api.get(`/grades/course/${active.id}`).then((r) => setGrades(r.data));
    api.get(`/attendance/course/${active.id}`).then((r) => setAttendance(r.data));
    api.get(`/homeworks/course/${active.id}`).then((r) => setHomeworks(r.data));
  }, [active]);

  // Mobile: scroll active tab into view on change
  useEffect(() => {
    const el = tabsListRef.current;
    if (!el || window.innerWidth >= 768) return;
    const activeEl = el.querySelector('[data-state="active"]');
    if (activeEl && typeof activeEl.scrollIntoView === "function") {
      activeEl.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [tab]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-12 fade-in" data-testid="teacher-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C86444]">Espace Enseignant</span>
          <h1 className="mt-2 font-[Outfit] font-semibold text-3xl md:text-4xl tracking-tight text-[#1A2547]">Bonjour, {user?.name}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <NotificationToggle compact />
          <ChangePasswordDialog mustChange={user?.must_change_password} />
          <SignatureGate />
        </div>
      </div>

      {user?.must_change_password && (
        <div className="mt-6 bg-[#FFF5E5] border border-[#C86444]/30 rounded-2xl p-4 text-sm text-[#1A2547] flex items-start gap-2">
          <KeyRound className="w-5 h-5 text-[#C86444] shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <strong>Veuillez changer votre mot de passe</strong>
            <div className="text-[#5c6374]">Pour votre sécurité, modifiez le mot de passe temporaire qui vous a été communiqué.</div>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} orientation="vertical" className="mt-8 flex flex-col md:flex-row md:gap-6">
        <TabsList ref={tabsListRef} className="mobile-tabs-fade bg-[#F4F1EB] p-1.5 md:p-2 flex md:flex-col md:items-stretch md:w-60 md:shrink-0 md:h-auto md:rounded-2xl md:sticky md:top-24 md:self-start rounded-full h-auto overflow-x-auto md:overflow-visible gap-1 justify-start max-w-full">
          <TabsTrigger value="courses" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="teacher-tab-courses">
            <BookOpen className="w-4 h-4 mr-1.5" /> Mes cours
          </TabsTrigger>
          <TabsTrigger value="messages" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="teacher-tab-messages">
            <MessageSquare className="w-4 h-4 mr-1.5" /> Messagerie
          </TabsTrigger>
          {user?.is_compensated && (
            <TabsTrigger value="hours" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="teacher-tab-hours">
              <Wallet className="w-4 h-4 mr-1.5" /> Mes heures
            </TabsTrigger>
          )}
        </TabsList>

        <div className="flex-1 min-w-0">
        <TabsContent value="courses" className="mt-6 md:mt-0">
          <div className="grid lg:grid-cols-[300px_1fr] gap-6">
            <aside className="space-y-2">
              <div className="text-xs uppercase tracking-[0.18em] text-[#8a968f] mb-2">Mes cours</div>
              {courses.length === 0 && <div className="text-sm text-[#5C6861]">Aucun cours assigné. Demandez à l'administration de vous attribuer un cours.</div>}
              {courses.map((c) => (
                <button key={c.id} onClick={() => setActive(c)}
                  className={`block w-full text-left p-4 rounded-xl border transition-colors ${active?.id === c.id ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white border-[#EAE6DF] hover:border-[#1E3A8A]"}`}
                  data-testid={`teacher-course-${c.id}`}>
                  <div className="font-medium text-sm">{ML[c.matiere]} · {c.group_name || "—"}</div>
                  <div className={`text-xs mt-1 ${active?.id === c.id ? "text-white/70" : "text-[#8a968f]"}`}>{c.schedule_day} · {c.schedule_time}</div>
                </button>
              ))}
            </aside>

            <main>
              {!active && <div className="bg-white border border-[#EAE6DF] rounded-2xl p-10 text-center text-[#5C6861]">Sélectionnez un cours pour voir les détails.</div>}
              {active && (
                <div className="space-y-6">
                  <div className="bg-white border border-[#EAE6DF] rounded-2xl p-6">
                    <h2 className="font-[Outfit] font-semibold text-2xl text-[#1A2547]">{active.title}</h2>
                    <p className="text-sm text-[#5C6861] mt-1">{students.length} élève(s) · {active.schedule_day} · {active.schedule_time}</p>
                  </div>

              <Section title="Devoirs" right={
                <AddHomeworkDialog courseId={active.id} students={students}
                  onAdd={() => api.get(`/homeworks/course/${active.id}`).then((r) => setHomeworks(r.data))} />
              }>
                {homeworks.length === 0 && <div className="text-sm text-[#5c6374] p-4 text-center">Aucun devoir donné pour ce cours.</div>}
                {homeworks.map((hw) => (
                  <div key={hw.id} className="py-3 border-b border-[#EAE6DF] last:border-0" data-testid={`hw-${hw.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <strong className="text-[#1A2547]">{hw.title}</strong>
                          {hw.audience === "individual" ? (
                            <Badge className="bg-[#FBE8DC] text-[#C86444] border-0 rounded-full">
                              {students.find((s) => s.request_id === hw.student_request_id)?.student?.name || "Individuel"}
                            </Badge>
                          ) : <Badge className="bg-[#E0E7FF] text-[#1E3A8A] border-0 rounded-full">Tout le groupe</Badge>}
                          <span className="text-xs text-[#8a92a3]">À rendre : {new Date(hw.due_date).toLocaleDateString("fr-FR")}</span>
                        </div>
                        {hw.description && <p className="text-sm text-[#5c6374] mt-1">{hw.description}</p>}
                        {hw.attachment_path && <a href={`${API}/files/${hw.attachment_path}`} target="_blank" rel="noreferrer" className="text-xs text-[#1E3A8A] underline">Pièce jointe</a>}
                        <div className="text-xs text-[#8a92a3] mt-1">
                          {hw.completions?.filter((c) => c.done).length || 0} / {hw.audience === "individual" ? 1 : students.length} fait
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={async () => {
                        if (!window.confirm("Supprimer ce devoir ?")) return;
                        await api.delete(`/homeworks/${hw.id}`);
                        toast.success("Supprimé");
                        api.get(`/homeworks/course/${active.id}`).then((r) => setHomeworks(r.data));
                      }} data-testid={`delete-hw-${hw.id}`}><Trash2 className="w-4 h-4 text-[#B0413E]" /></Button>
                    </div>
                  </div>
                ))}
              </Section>

              <Section title="Élèves & Présence">
                {students.length === 0 && <div className="text-sm text-[#5C6861] p-4">Aucun élève inscrit.</div>}
                {students.map((s) => (
                  <StudentRow key={s.id} enrollment={s} courseId={active.id} attendance={attendance}
                    onAction={() => api.get(`/attendance/course/${active.id}`).then((r) => setAttendance(r.data))} />
                ))}
              </Section>

              <Section title="Notes" right={
                <AddGradeDialog courseId={active.id} students={students}
                  onAdd={() => api.get(`/grades/course/${active.id}`).then((r) => setGrades(r.data))} />
              }>
                <SimpleTable
                  headers={["Élève", "Évaluation", "Note", "Commentaire"]}
                  rows={grades.map((g) => [
                    students.find((s) => s.request_id === g.student_request_id)?.student?.name || "—",
                    g.title, `${g.score} / ${g.max_score}`, g.comment || "—",
                  ])}
                  empty="Aucune note enregistrée." />
              </Section>

              <Section title="Présences récentes">
                <SimpleTable
                  headers={["Élève", "Date", "Statut", "Motif", ""]}
                  rows={attendance.slice(-30).reverse().map((a) => [
                    students.find((s) => s.request_id === a.student_request_id)?.student?.name || "—",
                    new Date(a.date).toLocaleDateString("fr-FR"),
                    <Badge key={`${a.id}-s`} className={`border-0 rounded-full ${a.status === "present" ? "bg-[#E0F2E9] text-[#1E7A4D]" : a.status === "absent" ? "bg-[#FBE5E4] text-[#B0413E]" : "bg-[#FFF5E5] text-[#C86444]"}`}>
                      {a.status === "present" ? "Présent" : a.status === "absent" ? "Absent" : "Retard"}
                    </Badge>,
                    a.note ? <span key={`${a.id}-n`} className="text-xs italic text-[#5c6374]">« {a.note} »</span> : "—",
                    <Button key={`${a.id}-d`} size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`delete-attendance-${a.id}`} onClick={async () => {
                      if (!window.confirm("Supprimer cette saisie ?")) return;
                      try {
                        await api.delete(`/attendance/${a.id}`);
                        toast.success("Saisie supprimée");
                        api.get(`/attendance/course/${active.id}`).then((r) => setAttendance(r.data));
                      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    }}><Trash2 className="w-4 h-4 text-[#B0413E]" /></Button>,
                  ])}
                  empty="Aucune présence enregistrée." />
              </Section>
            </div>
          )}
            </main>
          </div>
        </TabsContent>

        <TabsContent value="messages" className="mt-6 md:mt-0">
          <MessagingPanel currentUser={user} context="teacher" />
        </TabsContent>

        {user?.is_compensated && (
          <TabsContent value="hours" className="mt-6 md:mt-0">
            <TeacherHoursPanel user={user} courses={courses} />
          </TabsContent>
        )}
        </div>
      </Tabs>
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <div className="bg-white border border-[#EAE6DF] rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-[Outfit] font-semibold text-lg text-[#1A2547]">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function StudentRow({ enrollment, courseId, attendance, onAction }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [absentDialog, setAbsentDialog] = useState(false);
  const [note, setNote] = useState("");

  // Per-student stats for this course (computed from props)
  const myRecords = (attendance || []).filter((a) => a.student_request_id === enrollment.request_id);
  const total = myRecords.length;
  const present = myRecords.filter((r) => r.status === "present").length;
  const absent = myRecords.filter((r) => r.status === "absent").length;
  const retard = myRecords.filter((r) => r.status === "retard").length;
  const rate = total > 0 ? Math.round(((present + retard) / total) * 100) : null;

  const save = async (status, reason = null) => {
    try {
      await api.post("/attendance", { course_id: courseId, student_request_id: enrollment.request_id, date, status, note: reason });
      toast.success(status === "present" ? "Présent" : status === "retard" ? "Retard noté" : "Absence enregistrée");
      onAction();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitAbsence = async () => {
    await save("absent", note || null);
    setAbsentDialog(false);
    setNote("");
  };

  return (
    <div className="flex flex-wrap items-center gap-3 py-3 border-b border-[#EAE6DF] last:border-0" data-testid={`student-row-${enrollment.request_id}`}>
      <div className="flex-1 min-w-[180px]">
        <div className="text-sm font-medium text-[#1A2547]">{enrollment.student?.name}</div>
        <div className="text-xs text-[#8a968f]">Parent: {enrollment.student?.parent_name} · {enrollment.student?.parent_phone}</div>
        <StudentLevelBadges student={enrollment.student} requestId={enrollment.request_id} onUpdated={onAction} />
        {total > 0 && (
          <div className="mt-1.5 flex items-center gap-2 flex-wrap" data-testid={`stats-inline-${enrollment.request_id}`}>
            <Badge className={`border-0 rounded-full text-[10px] ${rate >= 90 ? "bg-[#E0F2E9] text-[#1E7A4D]" : rate >= 70 ? "bg-[#FFF5E5] text-[#C86444]" : "bg-[#FBE5E4] text-[#B0413E]"}`}>
              {rate}% présence
            </Badge>
            {absent > 0 && <span className="text-[11px] text-[#B0413E]">{absent} abs.</span>}
            {retard > 0 && <span className="text-[11px] text-[#C86444]">{retard} retard{retard > 1 ? "s" : ""}</span>}
          </div>
        )}
      </div>
      <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40 rounded-lg" data-testid={`date-${enrollment.request_id}`} />
      <Button size="sm" variant="outline" className="rounded-full border-[#1E7A4D] text-[#1E7A4D] hover:bg-[#E0F2E9]" onClick={() => save("present")} data-testid={`mark-present-${enrollment.request_id}`}>Présent</Button>
      <Button size="sm" variant="outline" className="rounded-full border-[#C86444] text-[#C86444] hover:bg-[#FFF5E5]" onClick={() => save("retard")} data-testid={`mark-retard-${enrollment.request_id}`}>Retard</Button>
      <Button size="sm" variant="outline" className="rounded-full border-[#B0413E] text-[#B0413E] hover:bg-[#FBE5E4]" onClick={() => setAbsentDialog(true)} data-testid={`mark-absent-${enrollment.request_id}`}>Absent</Button>
      <AttendanceHistoryDialog studentName={enrollment.student?.name} requestId={enrollment.request_id} courseId={courseId} />

      <Dialog open={absentDialog} onOpenChange={setAbsentDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Marquer absent — {enrollment.student?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-[#5c6374]">Date : <strong>{new Date(date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</strong></div>
            <div>
              <Label className="text-sm">Motif (optionnel)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex: Maladie, justifié par les parents…" rows={3} className="rounded-xl mt-1.5" data-testid={`absent-note-${enrollment.request_id}`} />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" className="rounded-full" onClick={() => { setAbsentDialog(false); setNote(""); }}>Annuler</Button>
              <Button className="rounded-full bg-[#B0413E] hover:bg-[#8E2F2D]" onClick={submitAbsence} data-testid={`confirm-absent-${enrollment.request_id}`}>Enregistrer l'absence</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AttendanceHistoryDialog({ studentName, requestId, courseId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  const reload = useCallback(() => {
    api.get(`/attendance/student/${requestId}`, { params: { course_id: courseId } })
      .then((r) => setData(r.data))
      .catch(() => setData({ records: [], stats: { total: 0, present: 0, absent: 0, retard: 0, presence_rate: null } }));
  }, [requestId, courseId]);
  useEffect(() => { if (open) reload(); }, [open, reload]);

  const deleteRecord = async (aid) => {
    if (!window.confirm("Supprimer cette saisie ?")) return;
    try {
      await api.delete(`/attendance/${aid}`);
      toast.success("Saisie supprimée");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const STATUS_LABEL = { present: "Présent", absent: "Absent", retard: "Retard" };
  const STATUS_STYLE = {
    present: "bg-[#E0F2E9] text-[#1E7A4D]",
    absent: "bg-[#FBE5E4] text-[#B0413E]",
    retard: "bg-[#FFF5E5] text-[#C86444]",
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="rounded-full text-[#1E3A8A]" data-testid={`history-${requestId}`}>
          <History className="w-4 h-4 mr-1.5" /> Historique
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Présences — {studentName}</DialogTitle></DialogHeader>
        {!data && <div className="text-sm text-[#5c6374] py-6 text-center">Chargement…</div>}
        {data && (
          <>
            <div className="grid grid-cols-4 gap-2">
              <StatTile label="Taux" value={data.stats.presence_rate != null ? `${data.stats.presence_rate}%` : "—"} accent="#1E3A8A" />
              <StatTile label="Présent" value={data.stats.present} accent="#1E7A4D" />
              <StatTile label="Retards" value={data.stats.retard} accent="#C86444" />
              <StatTile label="Absences" value={data.stats.absent} accent="#B0413E" />
            </div>
            <div className="mt-4 border-t border-[#EAE6DF] pt-4">
              <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8a92a3] mb-2">Détail ({data.records.length})</h4>
              {data.records.length === 0 && <div className="text-sm text-[#5c6374] py-4 text-center">Aucune présence enregistrée.</div>}
              <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
                {data.records.map((r) => (
                  <div key={r.id} className="flex items-start gap-2 text-sm bg-[#FCFBF8] border border-[#EAE6DF] rounded-lg p-2.5">
                    <Badge className={`border-0 rounded-full text-[10px] shrink-0 ${STATUS_STYLE[r.status]}`}>{STATUS_LABEL[r.status]}</Badge>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-[#1A2547]">{new Date(r.date).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</div>
                      {r.note && <div className="text-xs text-[#5c6374] mt-0.5 italic">« {r.note} »</div>}
                    </div>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 shrink-0" onClick={() => deleteRecord(r.id)} data-testid={`history-delete-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-[#B0413E]" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatTile({ label, value, accent }) {
  return (
    <div className="bg-[#FCFBF8] border border-[#EAE6DF] rounded-xl p-3 text-center">
      <div className="text-xl font-[Outfit] font-semibold" style={{ color: accent }}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-[#8a92a3] mt-0.5">{label}</div>
    </div>
  );
}

function AddGradeDialog({ courseId, students, onAdd }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_request_id: "", title: "", score: "", max_score: "20", comment: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/grades", {
        course_id: courseId, student_request_id: form.student_request_id, title: form.title,
        score: parseFloat(form.score), max_score: parseFloat(form.max_score), comment: form.comment || null,
      });
      toast.success("Note enregistrée");
      setOpen(false); setForm({ student_request_id: "", title: "", score: "", max_score: "20", comment: "" }); onAdd();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-full bg-[#1E3A8A] hover:bg-[#14265E]">+ Ajouter</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nouvelle note</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="text-sm">Élève</Label>
            <Select value={form.student_request_id} onValueChange={(v) => setForm({ ...form, student_request_id: v })}>
              <SelectTrigger className="rounded-xl mt-1.5"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
              <SelectContent>{students.map((s) => <SelectItem key={s.request_id} value={s.request_id}>{s.student?.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-sm">Titre</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-sm">Note</Label><Input required type="number" step="0.01" value={form.score} onChange={(e) => setForm({ ...form, score: e.target.value })} className="rounded-xl mt-1.5" /></div>
            <div><Label className="text-sm">Sur</Label><Input required type="number" step="0.01" value={form.max_score} onChange={(e) => setForm({ ...form, max_score: e.target.value })} className="rounded-xl mt-1.5" /></div>
          </div>
          <div><Label className="text-sm">Commentaire</Label><Input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} className="rounded-xl mt-1.5" /></div>
          <Button type="submit" className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E]">Enregistrer</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SimpleTable({ headers, rows, empty }) {
  if (rows.length === 0) return <div className="text-sm text-[#5C6861] p-4 text-center">{empty}</div>;
  return (
    <table className="w-full">
      <thead><tr className="border-b border-[#EAE6DF]">
        {headers.map((h) => <th key={h} className="text-left px-3 py-2 text-xs uppercase tracking-[0.18em] font-semibold text-[#8a968f]">{h}</th>)}
      </tr></thead>
      <tbody>{rows.map((r, i) => (
        <tr key={`row-${i}-${typeof r[0] === "string" ? r[0] : ""}`} className="border-b border-[#EAE6DF] last:border-0">
          {r.map((c, j) => <td key={`${i}-${j}`} className="px-3 py-3 text-sm text-[#1A2547]">{c}</td>)}
        </tr>
      ))}</tbody>
    </table>
  );
}

function AddHomeworkDialog({ courseId, students, onAdd }) {
  const empty = { title: "", description: "", due_date: "", attachment_path: "", audience: "course", student_request_id: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/upload`, { method: "POST", body: fd, credentials: "include" });
      if (!r.ok) {
        const errData = await r.json().catch(() => ({}));
        throw new Error(errData.detail || "Échec de l'upload");
      }
      const data = await r.json();
      setForm((f) => ({ ...f, attachment_path: data.path }));
      toast.success("Pièce jointe téléchargée");
    } catch (err) { toast.error(err.message); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.audience === "individual" && !form.student_request_id) {
      return toast.error("Sélectionnez un élève");
    }
    try {
      await api.post("/homeworks", {
        course_id: courseId,
        title: form.title,
        description: form.description || null,
        due_date: form.due_date,
        attachment_path: form.attachment_path || null,
        audience: form.audience,
        student_request_id: form.audience === "individual" ? form.student_request_id : null,
      });
      toast.success("Devoir créé");
      setOpen(false); setForm(empty); onAdd();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="add-homework-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Donner un devoir
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Nouveau devoir</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="text-sm">Titre *</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl mt-1.5" placeholder="Ex: Apprendre la sourate Al-Fatiha" data-testid="hw-title" /></div>
          <div><Label className="text-sm">Description / consignes</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1.5" rows={3} /></div>
          <div><Label className="text-sm">À rendre pour le *</Label><Input required type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-xl mt-1.5" data-testid="hw-due" /></div>
          <div>
            <Label className="text-sm">Pour qui ?</Label>
            <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="hw-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="course">Tout le groupe</SelectItem>
                <SelectItem value="individual">Un élève en particulier</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.audience === "individual" && (
            <div><Label className="text-sm">Élève</Label>
              <Select value={form.student_request_id} onValueChange={(v) => setForm({ ...form, student_request_id: v })}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="hw-student"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  {students.map((s) => <SelectItem key={s.request_id} value={s.request_id}>{s.student?.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-sm">Pièce jointe (PDF ou image, optionnel)</Label>
            <div className="flex items-center gap-2 mt-1.5">
              <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={handleFile} className="hidden" data-testid="hw-file-input" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full" data-testid="hw-upload-btn">
                <Upload className="w-4 h-4 mr-1.5" /> {uploading ? "Téléchargement…" : form.attachment_path ? "Remplacer" : "Téléverser"}
              </Button>
              {form.attachment_path && <span className="text-xs text-[#1E7A4D]">✓ Téléchargé</span>}
            </div>
          </div>
          <Button type="submit" className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="hw-submit">Enregistrer</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TeacherHoursPanel({ user, courses }) {
  const [data, setData] = useState({ records: [], stats: { total_hours: 0, validated_hours: 0, pending_hours: 0, validated_amount: 0, pending_amount: 0 } });
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [openAdd, setOpenAdd] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), hours: "", course_id: "none", note: "" });

  const load = useCallback(() => {
    api.get("/teacher/work-hours", { params: { month } })
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/work-hours", {
        date: form.date,
        hours: parseFloat(form.hours),
        course_id: form.course_id !== "none" ? form.course_id : null,
        note: form.note || null,
      });
      toast.success("Heures déclarées · en attente de validation");
      setOpenAdd(false);
      setForm({ date: new Date().toISOString().slice(0, 10), hours: "", course_id: "none", note: "" });
      load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };

  const remove = async (wid) => {
    if (!window.confirm("Supprimer cette saisie ?")) return;
    try { await api.delete(`/work-hours/${wid}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const STATUS = {
    pending: { label: "En attente", style: "bg-[#FFF5E5] text-[#C86444]" },
    validated: { label: "Validée", style: "bg-[#E0F2E9] text-[#1E7A4D]" },
    rejected: { label: "Rejetée", style: "bg-[#FBE5E4] text-[#B0413E]" },
  };

  return (
    <div className="space-y-6" data-testid="teacher-hours-panel">
      <div className="bg-white border border-[#EAE6DF] rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-[Outfit] font-semibold text-xl text-[#1A2547]">Suivi de mes heures</h3>
            <p className="text-sm text-[#5c6374] mt-1">
              Tarif horaire : <strong className="text-[#1E3A8A]">{user.hourly_rate ?? 0} €/h</strong>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 rounded-xl" data-testid="hours-month" />
            <Dialog open={openAdd} onOpenChange={setOpenAdd}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="hours-add-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Déclarer des heures
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Déclarer des heures</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                  <div><Label className="text-sm">Date *</Label><Input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl mt-1.5" data-testid="hours-date" /></div>
                  <div>
                    <Label className="text-sm">Durée (heures) *</Label>
                    <Input required type="number" step="0.25" min="0.25" max="24" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className="rounded-xl mt-1.5" placeholder="Ex: 1.5" data-testid="hours-amount" />
                  </div>
                  <div>
                    <Label className="text-sm">Cours (optionnel)</Label>
                    <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })}>
                      <SelectTrigger className="rounded-xl mt-1.5" data-testid="hours-course"><SelectValue placeholder="Aucun cours spécifique" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun cours spécifique</SelectItem>
                        {courses.map((c) => <SelectItem key={c.id} value={c.id}>{ML[c.matiere]} · {c.group_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Note (optionnel)</Label>
                    <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="rounded-xl mt-1.5" rows={2} placeholder="Ex: Cours du samedi, préparation du conseil…" data-testid="hours-note" />
                  </div>
                  <Button type="submit" className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="hours-submit">Enregistrer</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Heures validées", value: `${data.stats.validated_hours} h`, color: "#1E7A4D" },
          { label: "Heures en attente", value: `${data.stats.pending_hours} h`, color: "#C86444" },
          { label: "Montant validé", value: `${data.stats.validated_amount.toFixed(2)} €`, color: "#1E3A8A" },
          { label: "Montant prévisionnel", value: `${data.stats.pending_amount.toFixed(2)} €`, color: "#5c6374" },
        ].map((t) => (
          <div key={t.label} className="bg-white border border-[#EAE6DF] rounded-2xl p-5">
            <div className="text-2xl md:text-3xl font-[Outfit] font-semibold" style={{ color: t.color }}>{t.value}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a92a3] mt-1.5">{t.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-[#EAE6DF] rounded-2xl overflow-x-auto">
        <div className="px-6 py-4 border-b border-[#EAE6DF]">
          <h3 className="font-[Outfit] font-semibold text-base text-[#1A2547]">Mes saisies ({data.records.length})</h3>
        </div>
        <table className="w-full min-w-[560px]">
          <thead><tr className="border-b border-[#EAE6DF] bg-[#FCFBF8]">
            {["Date", "Cours / Note", "Heures", "Montant", "Statut", ""].map((h) => (
              <th key={h} className="text-left px-6 py-3 text-xs uppercase tracking-[0.18em] font-semibold text-[#8a92a3]">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {data.records.length === 0 && (
              <tr><td colSpan="6" className="px-6 py-10 text-center text-sm text-[#5c6374]">Aucune heure déclarée pour ce mois.</td></tr>
            )}
            {data.records.map((r) => (
              <tr key={r.id} className="border-b border-[#EAE6DF] last:border-0" data-testid={`hours-row-${r.id}`}>
                <td className="px-6 py-3 text-sm">{new Date(r.date).toLocaleDateString("fr-FR")}</td>
                <td className="px-6 py-3 text-sm">
                  <div className="text-[#1A2547]">{r.course_label || "—"}</div>
                  {r.note && <div className="text-xs text-[#5c6374] italic">« {r.note} »</div>}
                  {r.rejection_reason && <div className="text-xs text-[#B0413E] italic">Rejet : {r.rejection_reason}</div>}
                </td>
                <td className="px-6 py-3 text-sm">{r.hours.toFixed(2)} h</td>
                <td className="px-6 py-3 text-sm font-medium">{(r.amount_snapshot || 0).toFixed(2)} €</td>
                <td className="px-6 py-3 text-sm">
                  <Badge className={`border-0 rounded-full ${STATUS[r.status]?.style}`}>{STATUS[r.status]?.label}</Badge>
                </td>
                <td className="px-6 py-3 text-sm">
                  {r.status === "pending" && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => remove(r.id)} data-testid={`hours-delete-${r.id}`} title="Supprimer (uniquement si en attente)">
                      <Trash2 className="w-4 h-4 text-[#B0413E]" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function StudentLevelBadges({ student, requestId, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    niveau_arabe_ecrit: student?.niveau_arabe_ecrit || 0,
    niveau_arabe_oral: student?.niveau_arabe_oral || 0,
    nb_sourates: student?.nb_sourates || 0,
    sourates_connues: student?.sourates_connues || [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        niveau_arabe_ecrit: student?.niveau_arabe_ecrit || 0,
        niveau_arabe_oral: student?.niveau_arabe_oral || 0,
        nb_sourates: student?.nb_sourates || 0,
        sourates_connues: student?.sourates_connues || [],
      });
    }
    // eslint-disable-next-line
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/teacher/students/${requestId}/level`, {
        niveau_arabe_ecrit: Number(form.niveau_arabe_ecrit),
        niveau_arabe_oral: Number(form.niveau_arabe_oral),
        nb_sourates: Number(form.nb_sourates),
        sourates_connues: Array.isArray(form.sourates_connues) ? form.sourates_connues : [],
      });
      toast.success("Niveau mis à jour");
      setOpen(false);
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const ne = student?.niveau_arabe_ecrit || 0;
  const no_ = student?.niveau_arabe_oral || 0;
  const ns = student?.nb_sourates || 0;
  const sourateNames = useMemo(
    () => (student?.sourates_connues || []).map((n) => SOURATES.find((s) => s.num === n)?.name).filter(Boolean),
    [student]
  );
  const recent = sourateNames.slice(-3);

  return (
    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid={`level-badges-${requestId}`}>
      <Badge className="bg-[#E0E7FF] text-[#1E3A8A] hover:bg-[#E0E7FF] border-0 rounded-full text-[10px]">
        Écrit : {LEVEL_LABELS[ne]}
      </Badge>
      <Badge className="bg-[#E0E7FF] text-[#1E3A8A] hover:bg-[#E0E7FF] border-0 rounded-full text-[10px]">
        Oral : {LEVEL_LABELS[no_]}
      </Badge>
      <Badge className="bg-[#FBE8DC] text-[#C86444] hover:bg-[#FBE8DC] border-0 rounded-full text-[10px]">
        {ns} sourate{ns > 1 ? "s" : ""}
      </Badge>
      {recent.length > 0 && (
        <span className="text-[10px] text-[#8a968f] italic" title={sourateNames.join(", ")}>
          dont {recent.join(", ")}…
        </span>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-[#1E3A8A] rounded-full" data-testid={`edit-level-${requestId}`}>
            <Pencil className="w-3 h-3 mr-1" /> Modifier
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Niveau de {student?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <ArabicLevelBlock form={form} set={set} testIdPrefix={`teacher-${requestId}`} />
            <Button type="submit" disabled={saving} className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid={`save-level-${requestId}`}>
              {saving ? "Enregistrement…" : "Enregistrer le niveau"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

